import React, { useEffect, useState } from "react";
import {
  CreateQuestion,
  GetSurveyAndQuestionDetails,
} from "../../../services/SurveyService";
import { GetAnswerTypes } from "../../../services/metadataService";
import { useParams } from "react-router-dom";
import NormalAlert from "../../AlertComponent/NormalAlert";

const CreateQuestions = () => {
  const { assessmentUID } = useParams();
  const [questionText, setQuestionText] = useState("");
  const [answerType, setAnswerType] = useState("");
  const [optionList, setOptionList] = useState([{ option: "" }]);
  const [answerTypes, setAnswerTypes] = useState([]);
  const [questionList, setQuestionList] = useState([]);
  const [alert, setAlert] = useState(null);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);

  useEffect(() => {
    fetchAnswerTypes();
    fetchQuestions();
  }, []);

  const fetchAnswerTypes = async () => {
    try {
      const res = await GetAnswerTypes();
      if (Array.isArray(res)) {
        setAnswerTypes(res);
      }
    } catch (err) {
      console.error("Failed to fetch answer types", err);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await GetSurveyAndQuestionDetails(assessmentUID);
      if (res?.data?.questionList) {
        setQuestionList(res.data.questionList);
      }
    } catch (error) {
      console.error("Error fetching question list:", error);
    }
  };

  const handleOptionChange = (index, value) => {
    const updated = [...optionList];
    updated[index].option = value;
    setOptionList(updated);
  };

  const addOption = () => {
    if (parseInt(answerType) === 3 && optionList.length >= 2) {
      alert("Only two options are allowed for Single Choice questions.");
      return;
    }
    setOptionList([...optionList, { option: "" }]);
  };

  const removeOption = (index) => {
    const updated = [...optionList];
    updated.splice(index, 1);
    setOptionList(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const filteredOptions = [3, 4].includes(Number(answerType))
        ? optionList.filter((opt) => opt.option.trim() !== "")
        : [];

      const res = await CreateQuestion(
        assessmentUID,
        questionText,
        answerType,
        filteredOptions
      );

      if (res?.data?.status === "Success") {
        setAlert({
          type: "success",
          message: res.data.message || "Question created successfully",
        });
        const updatedQuestions = await GetSurveyAndQuestionDetails(assessmentUID);
        const newQuestionList = updatedQuestions?.data?.questionList || [];
        setQuestionList(newQuestionList);

        const lastIndex = newQuestionList.length - 1;
        const latestQuestion = newQuestionList[lastIndex];
        setSelectedQuestionIndex(lastIndex);
        setQuestionText(latestQuestion?.question || "");

        const typeMatch = answerTypes.find(
          (t) => t.answerType === latestQuestion?.answerType
        );
        setAnswerType(typeMatch ? String(typeMatch.answerTypeID) : "");

        setOptionList(
          latestQuestion?.options?.length
            ? latestQuestion.options.map((opt) => ({ option: opt.optionName }))
            : [{ option: "" }]
        );
      }
    } catch (err) {
      setAlert({
        type: "danger",
        message: err?.response?.data?.message || "Error creating question",
      });
    }
  };

  const resetForm = () => {
    setQuestionText("");
    setAnswerType("");
    setOptionList([{ option: "" }]);
    setSelectedQuestionIndex(null);
  };

  const handleQuestionClick = (index) => {
    const selected = questionList[index];
    setSelectedQuestionIndex(index);
    setQuestionText(selected.question || "");
    const typeMatch = answerTypes.find(
      (t) => t.answerType === selected.answerType
    );
    setAnswerType(typeMatch ? String(typeMatch.answerTypeID) : "");
    setOptionList(
      selected.options?.length > 0
        ? selected.options.map((opt) => ({ option: opt.optionName }))
        : [{ option: "" }]
    );
  };

  const parsedAnswerType = parseInt(answerType);
  const isOptionBased = parsedAnswerType === 3 || parsedAnswerType === 4;
  const isShortAnswer = parsedAnswerType === 1;
  const isLongAnswer = parsedAnswerType === 2;

  return (
    <div className="container mt-5 pt-4">
      <div className="row">
        {/* Left Panel */}
        <div className="col-md-4 col-lg-3 mb-4">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">Questions</h6>
            </div>
            <ul className="list-group list-group-flush">
              {questionList.length > 0 ? (
                questionList.map((q, i) => (
                  <li
                    key={i}
                    className={`list-group-item ${
                      selectedQuestionIndex === i ? "bg-light fw-bold" : ""
                    }`}
                    onClick={() => handleQuestionClick(i)}
                    style={{ cursor: "pointer" }}
                  >
                    Question {i + 1}
                  </li>
                ))
              ) : (
                <li className="list-group-item text-muted">
                  No questions yet.
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Right Panel */}
        <div className="col-md-8 col-lg-9">
          <div className="card shadow-sm">
            <div className="card-body">
              <h4 className="card-title mb-4">
                {selectedQuestionIndex !== null
                  ? `Edit Question ${selectedQuestionIndex + 1}`
                  : "Create New Question"}
              </h4>

              {alert && (
                <NormalAlert
                  type={alert.type}
                  message={alert.message}
                  onClose={() => setAlert(null)}
                />
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label fw-bold">Question</label>
                  <input
                    type="text"
                    className="form-control"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    required
                    placeholder="Enter your question"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-bold">Answer Type</label>
                  <select
                    className="form-select"
                    value={answerType}
                    onChange={(e) => setAnswerType(e.target.value)}
                    required
                  >
                    <option value="">-- Select Answer Type --</option>
                    {answerTypes.map((type) => (
                      <option key={type.answerTypeID} value={type.answerTypeID}>
                        {type.answerType}
                      </option>
                    ))}
                  </select>
                </div>

                {isOptionBased && (
                  <div className="mb-4">
                    <label className="form-label fw-bold">Options</label>
                    {optionList.map((opt, index) => (
                      <div className="input-group mb-2" key={index}>
                        <input
                          type="text"
                          className="form-control"
                          value={opt.option}
                          onChange={(e) =>
                            handleOptionChange(index, e.target.value)
                          }
                          placeholder={`Option ${index + 1}`}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeOption(index)}
                          disabled={optionList.length <= 1}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={addOption}
                      disabled={parsedAnswerType === 3 && optionList.length >= 2}
                    >
                      Add Option
                    </button>
                  </div>
                )}

                <div className="mb-4">
                  <label className="form-label fw-bold">Preview</label>
                  <div className="border p-3 rounded bg-light">
                    {isShortAnswer && (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Short answer..."
                      />
                    )}
                    {isLongAnswer && (
                      <textarea
                        className="form-control"
                        rows={4}
                        placeholder="Long answer..."
                      />
                    )}
                    {parsedAnswerType === 3 &&
                      optionList.map((opt, idx) => (
                        <div className="form-check" key={idx}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="previewRadio"
                          />
                          <label className="form-check-label">
                            {opt.option || `Option ${idx + 1}`}
                          </label>
                        </div>
                      ))}
                    {parsedAnswerType === 4 &&
                      optionList.map((opt, idx) => (
                        <div className="form-check" key={idx}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            disabled
                          />
                          <label className="form-check-label">
                            {opt.option || `Option ${idx + 1}`}
                          </label>
                        </div>
                      ))}
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={resetForm}
                  >
                    Clear
                  </button>
                  <button type="submit" className="btn btn-success px-4">
                    {selectedQuestionIndex !== null ? "Update" : "Submit"}{" "}
                    Question
                  </button>
                </div>
              </form>

              {/* Selected Question Preview Below */}
              {selectedQuestionIndex !== null && (
                <div className="mt-5 text-center">
                  <h5 className="fw-bold mb-3">Selected Question Preview</h5>
                  <div
                    className="border p-4 rounded bg-light d-inline-block text-start"
                    style={{ maxWidth: "600px", width: "100%" }}
                  >
                    <p>
                      <strong>Q:</strong>{" "}
                      {questionList[selectedQuestionIndex]?.question}
                    </p>
                    <p>
                      <strong>Answer Type:</strong>{" "}
                      {questionList[selectedQuestionIndex]?.answerType}
                    </p>
                    <div>
                      {questionList[selectedQuestionIndex]?.answerType ===
                        "Short Answer" && (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Short answer..."
                          disabled
                        />
                      )}

                      {questionList[selectedQuestionIndex]?.answerType ===
                        "Long Answer" && (
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Long answer..."
                          disabled
                        />
                      )}

                      {questionList[selectedQuestionIndex]?.answerType ===
                        "Single Choice" &&
                        questionList[selectedQuestionIndex]?.options?.map(
                          (opt, idx) => (
                            <div className="form-check" key={idx}>
                              <input
                                className="form-check-input"
                                type="radio"
                                disabled
                              />
                              <label className="form-check-label">
                                {opt.optionName}
                              </label>
                            </div>
                          )
                        )}

                      {questionList[selectedQuestionIndex]?.answerType ===
                        "Multiple Choice" &&
                        questionList[selectedQuestionIndex]?.options?.map(
                          (opt, idx) => (
                            <div className="form-check" key={idx}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                disabled
                              />
                              <label className="form-check-label">
                                {opt.optionName}
                              </label>
                            </div>
                          )
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateQuestions;
